import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Chart from './components/Chart';
import { FullScreenChartModal } from 'components';

import useModal from 'hooks/common/useModal';

import { Granularity } from './constants/granularity';
import { IHashrateChartProps } from './HashrateChart.types';

const DefaultGranularities = [Granularity.H5, Granularity.H1H, Granularity.H1D];

const HashrateChart = ({
  titleClass,
  title = 'page.dashboard.blocks.hashrateChart.title',
  column,
  aid,
  uid,
  onChangeColumn,
  requestCallback,
  granularities = DefaultGranularities,
  ...props
}: IHashrateChartProps) => {
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [granularity, setGranularity] = useState(column || granularities[0]);

  const { open, onOpen, onClose } = useModal();

  const handleChangeColumn = (value: Granularity) => {
    setGranularity(value);

    if (onChangeColumn) {
      onChangeColumn(value);
    }
  };

  const getChartData = useCallback(async () => {
    setLoading(true);

    const result = await requestCallback({
      column: granularity,
    });

    const { data, success } = result || {};

    if (data === undefined) {
      return;
    }

    if (success && data?.type !== 'error') {
      setData(data);
    } else {
      setData(null);
    }

    setLoading(false);
  }, [granularity, requestCallback]);

  useEffect(() => {
    getChartData();
  }, [aid, uid, getChartData]);

  return (
    <>
      <Chart
        enableExpend
        data={data}
        title={t(title)}
        loading={loading}
        titleClass={titleClass}
        granularity={granularity}
        granularities={granularities}
        onExpand={onOpen}
        onChangeGranularity={handleChangeColumn}
        {...props}
      />

      <FullScreenChartModal title={t(title)} open={open} onClose={onClose}>
        <Chart
          width="100%"
          height="100%"
          data={data}
          loading={loading}
          granularity={granularity}
          granularities={granularities}
          onChangeGranularity={handleChangeColumn}
          style={{ padding: '20px 0' }}
          {...props}
        />
      </FullScreenChartModal>
    </>
  );
};

export default HashrateChart;
